import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import Delete from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const MSG_TYPES = [
  { value: "text",   label: "Texto",   icon: <TextFieldsIcon fontSize="small" />,   color: "#1976d2" },
  { value: "audio",  label: "Audio",   icon: <AudiotrackIcon fontSize="small" />,   color: "#2e7d32" },
  { value: "image",  label: "Imagem",  icon: <ImageIcon fontSize="small" />,         color: "#0288d1" },
  { value: "video",  label: "Video",   icon: <VideoLibraryIcon fontSize="small" />,  color: "#ed6c02" },
  { value: "file",   label: "Arquivo", icon: <AttachFileIcon fontSize="small" />,    color: "#7b1fa2" },
  { value: "label",  label: "Etiqueta",icon: <LocalOfferIcon fontSize="small" />,   color: "#c62828" },

];

const emptyMessage = () => ({
  _tempId: Math.random(),
  type: "text",
  content: "",
  mediaFile: null,
  mediaName: null,
  mediaPath: null,
  delaySeconds: 0,
  simulatePresence: false,
  id: null
});

const LibraryPickerDialog = ({ open, onClose, onSelect }) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setTypeFilter("all");
    setCategoryFilter("all");
    setLoading(true);
    api.get("/quickAnswers", { params: { pageNumber: 1 } })
      .then(({ data }) => setMessages(data.quickAnswers || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const typeIconMap = {
    text:  <TextFieldsIcon fontSize="small" sx={{ color: "#1976d2" }} />,
    audio: <AudiotrackIcon fontSize="small" sx={{ color: "#2e7d32" }} />,
    image: <ImageIcon fontSize="small" sx={{ color: "#0288d1" }} />,
    video: <VideoLibraryIcon fontSize="small" sx={{ color: "#ed6c02" }} />,
    file:  <AttachFileIcon fontSize="small" sx={{ color: "#7b1fa2" }} />,
  };

  const getMsgType = (msg) => {
    if (msg.type) return msg.type;
    if (!msg.mediaPath) return "text";
    const ext = msg.mediaPath.split(".").pop().toLowerCase();
    if (["mp3","ogg","wav","aac","m4a","opus"].includes(ext)) return "audio";
    if (["mp4","avi","mov","webm"].includes(ext)) return "video";
    if (["jpg","jpeg","png","gif","webp"].includes(ext)) return "image";
    return "file";
  };

  const availableCategories = [...new Set(messages.map(m => m.category).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, "pt", { numeric: true }));

  const filtered = [...messages.filter(m => {
    const q = search.toLowerCase();
    const matchSearch = !q || (m.shortcut && m.shortcut.toLowerCase().includes(q)) || (m.message && m.message.toLowerCase().includes(q));
    const matchType = typeFilter === "all" || getMsgType(m) === typeFilter;
    const matchCat = categoryFilter === "all" || (m.category || "") === categoryFilter;
    return matchSearch && matchType && matchCat;
  })].sort((a, b) => (a.shortcut || "").localeCompare(b.shortcut || "", "pt", { numeric: true }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ backgroundColor: theme.palette.primary.main, color: "#fff", display: "flex", alignItems: "center", gap: 1, py: 1.5 }}>
        <LibraryBooksIcon />
        <Typography variant="subtitle1" fontWeight={700}>Selecionar da Biblioteca</Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2, borderBottom: "1px solid " + theme.palette.divider }}>
          <TextField size="small" fullWidth placeholder="Buscar mensagem..." value={search} onChange={e => setSearch(e.target.value)} sx={{ mb: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: "1rem", color: "text.disabled" }} /></InputAdornment> }} />
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: availableCategories.length > 0 ? 1 : 0 }}>
            {[{ key: "all", label: "Todos" }, ...MSG_TYPES.map(t => ({ key: t.value, label: t.label }))].map(({ key, label }) => (
              <Chip key={key} label={label} size="small" onClick={() => setTypeFilter(key)}
                variant={typeFilter === key ? "filled" : "outlined"} color={typeFilter === key ? "primary" : "default"}
                sx={{ cursor: "pointer", fontSize: "0.7rem" }} />
            ))}
          </Box>
          {availableCategories.length > 0 && (
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
              <Chip key="cat-all" label="Todas" size="small" onClick={() => setCategoryFilter("all")}
                variant={categoryFilter === "all" ? "filled" : "outlined"} color={categoryFilter === "all" ? "secondary" : "default"}
                sx={{ cursor: "pointer", fontSize: "0.7rem" }} />
              {availableCategories.map(cat => (
                <Chip key={cat} label={cat} size="small" onClick={() => setCategoryFilter(cat)}
                  variant={categoryFilter === cat ? "filled" : "outlined"} color={categoryFilter === cat ? "secondary" : "default"}
                  sx={{ cursor: "pointer", fontSize: "0.7rem" }} />
              ))}
            </Box>
          )}
        </Box>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={28} /></Box>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 380, overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body2" color="textSecondary">
                  {messages.length === 0 ? "Nenhuma mensagem cadastrada" : "Nenhum resultado"}
                </Typography>
              </Box>
            ) : filtered.map(msg => {
              const t = getMsgType(msg);
              return (
                <React.Fragment key={msg.id}>
                  <ListItemButton onClick={() => onSelect(msg)} sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 34 }}>{typeIconMap[t] || typeIconMap.text}</ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: "0.83rem" }}>{msg.shortcut}</Typography>}
                      secondary={msg.message
                        ? <Typography variant="caption" color="textSecondary" sx={{ display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden", fontSize: "0.72rem" }}>{msg.message}</Typography>
                        : <Typography variant="caption" color="textDisabled" sx={{ fontSize: "0.72rem" }}>{t !== "text" ? "Arquivo de " + (MSG_TYPES.find(x => x.value === t)?.label || t) : ""}</Typography>}
                    />
                  </ListItemButton>
                  <Divider component="li" />
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose} size="small">Cancelar</Button></DialogActions>
    </Dialog>
  );
};

const ScriptModal = ({ open, onClose, script, onSaved }) => {
  const theme = useTheme();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [productId, setProductId] = useState("");
  const [categories, setCategories] = useState([]);
  const [messages, setMessages] = useState([emptyMessage()]);
  const [saving, setSaving] = useState(false);
  const [tags, setTags] = useState([]);
  const [products, setProducts] = useState([]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const fileRefs = useRef({});

  useEffect(() => {
    if (!open) return;
    api.get("/tags").then(({ data }) => setTags(data.tags || data || [])).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data || [])).catch(() => {});
    const initialPid = script?.productId ? "?productId=" + script.productId : "";
    api.get("/categories" + initialPid).then(({ data }) => setCategories(data || [])).catch(() => {});

    if (script) {
      setName(script.name || "");
      setDescription(script.description || "");
      setCategory(script.category || "");
      setCategoryId(script.categoryId ? String(script.categoryId) : "");
      setProductId(script.productId ? String(script.productId) : "");
      // fetch full script to get complete message data (list only returns id+type)
      api.get("/scripts/" + script.id).then(({ data }) => {
        setMessages(data.messages?.length
          ? data.messages.map(m => ({ ...m, _tempId: Math.random(), mediaFile: null }))
          : [emptyMessage()]
        );
      }).catch(() => setMessages([emptyMessage()]));
    } else {
      setName(""); setDescription(""); setCategory(""); setCategoryId(""); setProductId("");
      setMessages([emptyMessage()]);
    }
  }, [open, script]);

  const updateMsg = (idx, field, value) =>
    setMessages(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));

  const addMessage = () => setMessages(prev => [...prev, emptyMessage()]);

  const handleImportFromLibrary = (quickAnswer) => {
    const t = quickAnswer.type || "text";
    setMessages(prev => [...prev, {
      _tempId: Math.random(), type: t, content: quickAnswer.message || "",
      mediaFile: null, mediaName: quickAnswer.shortcut || (quickAnswer.mediaPath ? quickAnswer.mediaPath.split("/").pop() : null),
      mediaPath: quickAnswer.mediaPath || null, delaySeconds: 0, simulatePresence: false,
      tagId: null, id: null, _fromLibrary: true, _quickAnswerId: quickAnswer.id,
    }]);
    setLibraryOpen(false);
    toast.success(quickAnswer.shortcut + " adicionado ao script");
  };

  const removeMessage = (idx) => setMessages(prev => prev.filter((_, i) => i !== idx));

  const moveUp = (idx) => {
    if (idx === 0) return;
    setMessages(prev => { const arr = [...prev]; [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]]; return arr; });
  };

  const moveDown = (idx) => {
    setMessages(prev => {
      if (idx === prev.length - 1) return prev;
      const arr = [...prev]; [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]]; return arr;
    });
  };

  const handleMediaChange = (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    updateMsg(idx, "mediaFile", file);
    updateMsg(idx, "mediaName", file.name);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Digite um nome para o script"); return; }
    if (!messages.length) { toast.error("Adicione pelo menos uma mensagem"); return; }
    setSaving(true);
    try {
      let savedScript;
      if (script?.id) {
        const { data } = await api.put("/scripts/" + script.id, { name, description, category: category || null, categoryId: categoryId ? Number(categoryId) : null, productId: productId ? Number(productId) : null });
        savedScript = data;
        for (const m of (script.messages || [])) {
          await api.delete("/scripts/" + script.id + "/messages/" + m.id).catch(() => {});
        }
      } else {
        const { data } = await api.post("/scripts", { name, description, category: category || null, categoryId: categoryId ? Number(categoryId) : null, productId: productId ? Number(productId) : null });
        savedScript = data;
      }

      for (let i = 0; i < messages.length; i++) {
        const msg = messages[i];
        const formData = new FormData();
        formData.append("position", String(i));
        formData.append("type", msg.type);
        formData.append("delaySeconds", String(msg.delaySeconds || 0));
        formData.append("simulatePresence", msg.simulatePresence ? "true" : "false");
        if (msg.type === "label") {
          if (msg.tagId) formData.append("tagId", String(msg.tagId));
        } else if (msg.type === "text") {
          formData.append("content", msg.content || "");
        } else {
          if (msg.mediaFile) formData.append("media", msg.mediaFile);
          if (!msg.mediaFile && msg.mediaPath) formData.append("existingMediaPath", msg.mediaPath);
          if (msg.content) formData.append("content", msg.content);
        }
        await api.post("/scripts/" + savedScript.id + "/messages", formData, { headers: { "Content-Type": "multipart/form-data" } });
      }

      const { data: full } = await api.get("/scripts/" + savedScript.id);
      toast.success("Script " + full.name + " salvo!");
      onSaved?.(full);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const getTypeInfo = (type) => MSG_TYPES.find(t => t.value === type) || MSG_TYPES[0];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: theme.palette.primary.main, color: "#fff", py: 1.5 }}>
          <Typography variant="h6" fontWeight={700}>
            {script ? "Editar Speed Mode: " + script.name : "Novo Speed Mode"}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", gap: 2, mb: 1.5, mt: 0.5 }}>
            <TextField label="Nome do Script" value={name} onChange={e => setName(e.target.value)} fullWidth required size="small" />
            <TextField label="Descricao (opcional)" value={description} onChange={e => setDescription(e.target.value)} fullWidth size="small" />
          </Box>

          <Box sx={{ display: "flex", gap: 2, mb: 2.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Produto (opcional)</InputLabel>
              <Select value={productId} label="Produto (opcional)" onChange={e => {
                  setProductId(e.target.value);
                  setCategoryId("");
                  setCategory("");
                  const pid = e.target.value;
                  api.get("/categories" + (pid ? "?productId=" + pid : "")).then(({ data }) => setCategories(data || [])).catch(() => {});
                }}>
                <MenuItem value="">Sem produto</MenuItem>
                {products.map(p => <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>)}
              </Select>
            </FormControl>


            <FormControl fullWidth size="small">
              <InputLabel>Categoria (opcional)</InputLabel>
              <Select
                value={categoryId}
                label="Categoria (opcional)"
                onChange={e => {
                  const cid = e.target.value;
                  setCategoryId(cid);
                  const cat = categories.find(c => String(c.id) === String(cid));
                  setCategory(cat ? cat.name : "");
                }}
              >
                <MenuItem value="">Sem categoria</MenuItem>
                {categories.map(c => (
                  <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Divider sx={{ mb: 2 }}>
            <Chip label={messages.length + " etapa(s)"} size="small" color="primary" />
          </Divider>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {messages.map((msg, idx) => {
              const typeInfo = getTypeInfo(msg.type);
              return (
                <Paper key={msg._tempId || msg.id} variant="outlined" sx={{ p: 1.5, borderLeft: "4px solid " + typeInfo.color, borderRadius: 1.5,
                  backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : typeInfo.color + "08",
                  transition: "box-shadow 0.2s", "&:hover": { boxShadow: 2 } }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 28 }}>
                      <Box sx={{ width: 22, height: 22, borderRadius: "50%", backgroundColor: typeInfo.color, color: "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", fontWeight: 700, mb: 0.3 }}>
                        {idx + 1}
                      </Box>
                      <IconButton size="small" onClick={() => moveUp(idx)} disabled={idx === 0} sx={{ p: 0 }}>
                        <ArrowUpward sx={{ fontSize: 14 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => moveDown(idx)} disabled={idx === messages.length - 1} sx={{ p: 0 }}>
                        <ArrowDownward sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>

                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel>Tipo</InputLabel>
                      <Select value={msg.type} label="Tipo" onChange={e => updateMsg(idx, "type", e.target.value)}>
                        {MSG_TYPES.map(t => (
                          <MenuItem key={t.value} value={t.value}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, color: t.color }}>
                              {t.icon}
                              <Typography variant="body2" sx={{ fontSize: "0.82rem" }}>{t.label}</Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Box sx={{ flex: 1 }}>
                      {msg.type === "label" ? (
                        <FormControl fullWidth size="small">
                          <InputLabel>Etiqueta</InputLabel>
                          <Select value={msg.tagId || ""} label="Etiqueta" onChange={e => updateMsg(idx, "tagId", e.target.value)}>
                            <MenuItem value=""><em>Selecionar etiqueta...</em></MenuItem>
                            {tags.map(tag => (
                              <MenuItem key={tag.id} value={tag.id}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: tag.color || "#999" }} />
                                  {tag.name}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : msg.type === "text" ? (
                        <TextField label="Texto da mensagem" value={msg.content || ""} onChange={e => updateMsg(idx, "content", e.target.value)}
                          multiline minRows={2} fullWidth size="small" />
                      ) : (
                        <Box>
                          <input type="file" ref={el => (fileRefs.current[idx] = el)} style={{ display: "none" }}
                            accept={msg.type === "audio" ? "audio/*" : msg.type === "image" ? "image/*" : msg.type === "video" ? "video/*" : "*/*"}
                            onChange={e => handleMediaChange(idx, e)} />
                          <Button variant="outlined" size="small" startIcon={typeInfo.icon}
                            onClick={() => fileRefs.current[idx]?.click()}
                            sx={{ mb: 0.5, borderColor: typeInfo.color, color: typeInfo.color, textTransform: "none",
                              "&:hover": { borderColor: typeInfo.color, backgroundColor: typeInfo.color + "10" } }}>
                            {msg.mediaFile ? msg.mediaFile.name : msg.mediaPath ? (msg.mediaName || msg.mediaPath.split("/").pop()) : "Selecionar " + typeInfo.label}
                          </Button>
                          {(msg.mediaFile || msg.mediaPath) && (
                            <TextField label="Legenda (opcional)" value={msg.content || ""} onChange={e => updateMsg(idx, "content", e.target.value)}
                              fullWidth size="small" sx={{ mt: 0.5 }} />
                          )}
                        </Box>
                      )}
                    </Box>

                    {msg.type !== "label" && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 130 }}>
                        <TextField label="Delay (seg)" type="number" value={msg.delaySeconds || 0}
                          onChange={e => updateMsg(idx, "delaySeconds", Number(e.target.value))}
                          size="small" inputProps={{ min: 0, max: 300 }} sx={{ width: 120 }} />
                        <Tooltip title={msg.type === "audio" ? "Simular gravando audio..." : "Simular digitando..."}>
                          <FormControlLabel control={
                            <Checkbox checked={msg.simulatePresence || false}
                              onChange={e => updateMsg(idx, "simulatePresence", e.target.checked)} size="small" />
                          } label={<Typography variant="caption">{msg.type === "audio" ? "Simular gravacao" : "Simular digitacao"}</Typography>} sx={{ m: 0 }} />
                        </Tooltip>
                      </Box>
                    )}

                    <Tooltip title="Remover etapa">
                      <IconButton size="small" onClick={() => removeMessage(idx)} color="error">
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {msg._fromLibrary && (
                    <Box sx={{ mt: 0.8, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <CheckCircleIcon sx={{ fontSize: "0.75rem", color: "success.main" }} />
                      <Typography variant="caption" color="success.main" sx={{ fontSize: "0.7rem" }}>Importado da biblioteca</Typography>
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Box>

          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Button startIcon={<AddCircleOutline />} onClick={addMessage} variant="outlined" fullWidth sx={{ textTransform: "none" }}>
              Nova Etapa
            </Button>
            <Button startIcon={<LibraryBooksIcon />} onClick={() => setLibraryOpen(true)} variant="outlined" color="primary" fullWidth sx={{ textTransform: "none" }}>
              Selecionar da Biblioteca
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={saving} variant="outlined" sx={{ textTransform: "none" }}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving} sx={{ textTransform: "none", px: 3 }}>
            {saving ? "Salvando..." : "Salvar Speed Mode"}
          </Button>
        </DialogActions>
      </Dialog>

      <LibraryPickerDialog open={libraryOpen} onClose={() => setLibraryOpen(false)} onSelect={handleImportFromLibrary} />
    </>
  );
};

export default ScriptModal;
